import { CheckCircle2, Circle } from 'lucide-react';
import type { RouteAlternative } from '../../lib/googleMaps';
import { plural } from '@/lib/plural';

interface RouteOptionCardsProps {
  routes: RouteAlternative[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
}

export function RouteOptionCards({
  routes,
  selectedIndex,
  onSelect,
  disabled = false,
}: RouteOptionCardsProps) {
  if (routes.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[13px] font-semibold text-text">
          Select Route <span className="text-xs font-normal text-text-2">({plural(routes.length, 'option')} found)</span>
        </label>
        <span className="text-[11px] text-text-2">Click route or polyline on map</span>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {routes.map((route) => {
          const isSelected = route.index === selectedIndex;
          return (
            <button
              key={route.index}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(route.index)}
              className={`relative flex flex-col justify-between rounded-lg border p-3 text-left transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-xs ring-1 ring-primary'
                  : 'border-border bg-surface hover:border-text-2/40 hover:bg-surface-2/40'
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 font-medium text-sm text-text">
                  {isSelected ? (
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <Circle className="h-4 w-4 text-text-2 shrink-0" />
                  )}
                  <span>Route {route.index + 1}</span>
                </div>
                {isSelected && (
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    Selected
                  </span>
                )}
              </div>

              <div className="mt-1 flex items-baseline gap-1.5 text-xs">
                <span className="font-semibold text-text font-mono">
                  {route.distanceKm.toFixed(1)} km
                </span>
                {route.durationText && (
                  <span className="text-text-2">· {route.durationText}</span>
                )}
              </div>

              {route.summary && (
                <p className="mt-1 truncate text-[11px] text-text-2" title={`via ${route.summary}`}>
                  via {route.summary}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
